import {
  ArrowPathIcon,
  ChartBarIcon,
  CheckCircleIcon,
  ClockIcon,
  PlayIcon,
  StopIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { useCallback, useState } from 'react';
import { toast } from 'sonner';

import { useEvaluationBatches } from '../../hooks/useEvaluationBatches';
import { Card } from '../ui/card';
import { Badge } from '../ui/catalyst/badge';
import { Button } from '../ui/catalyst/button';
import { Heading } from '../ui/catalyst/heading';
import { Text } from '../ui/catalyst/text';
import { CreateEvaluationModal } from './CreateEvaluationModal';
import { EvaluationDetails } from './EvaluationDetails';

export const DemandEvaluation = () => {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedBatchId, setSelectedBatchId] = useState<string | null>(null);
  const {
    batches,
    loading,
    error,
    refetch,
    startEvaluation,
    stopEvaluation,
    restartEvaluation,
  } = useEvaluationBatches({
    pausePolling: isCreateModalOpen || !!selectedBatchId,
  });

  const handleStart = useCallback(
    async (batchId: string) => {
      try {
        await startEvaluation(batchId);
        toast.success('Evaluation started');
        refetch();
      } catch (err) {
        toast.error('Failed to start evaluation');
      }
    },
    [startEvaluation, refetch],
  );

  const handleStop = useCallback(
    async (batchId: string) => {
      try {
        await stopEvaluation(batchId);
        toast.success('Evaluation stopped');
        refetch();
      } catch (err) {
        toast.error('Failed to stop evaluation');
      }
    },
    [stopEvaluation, refetch],
  );

  const handleRestart = useCallback(
    async (batchId: string) => {
      try {
        await restartEvaluation(batchId);
        toast.success('Evaluation restarted');
        refetch();
      } catch (err) {
        toast.error('Failed to restart evaluation');
      }
    },
    [restartEvaluation, refetch],
  );

  const handleCreateComplete = useCallback(() => {
    setIsCreateModalOpen(false);
    refetch();
    toast.success('Evaluation batch created successfully');
  }, [refetch]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'zinc';
      case 'running':
        return 'blue';
      case 'completed':
        return 'green';
      case 'failed':
        return 'red';
      case 'cancelled':
        return 'orange';
      default:
        return 'zinc';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <ClockIcon className="h-4 w-4" />;
      case 'running':
        return <PlayIcon className="h-4 w-4" />;
      case 'completed':
        return <CheckCircleIcon className="h-4 w-4" />;
      case 'failed':
        return <XCircleIcon className="h-4 w-4" />;
      case 'cancelled':
        return <StopIcon className="h-4 w-4" />;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <Text className="text-red-600">Failed to load evaluations</Text>
      </div>
    );
  }

  if (selectedBatchId) {
    return (
      <EvaluationDetails
        batchId={selectedBatchId}
        onBack={() => setSelectedBatchId(null)}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <Heading level={2}>Demand Evaluation</Heading>
          <Text className="text-gray-600 mt-1">
            Run batch evaluations against gold standards and rubrics
          </Text>
        </div>
        <Button
          onClick={() => setIsCreateModalOpen(true)}
          className="flex items-center gap-2"
        >
          <PlayIcon className="h-4 w-4" />
          Create Evaluation
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <Card className="p-4">
          <Text className="text-sm text-gray-600">Total Batches</Text>
          <Text className="text-2xl font-semibold">{batches?.length || 0}</Text>
        </Card>

        <Card className="p-4">
          <Text className="text-sm text-gray-600">Running</Text>
          <Text className="text-2xl font-semibold text-blue-600">
            {batches?.filter((b) => b.status === 'running').length || 0}
          </Text>
        </Card>

        <Card className="p-4">
          <Text className="text-sm text-gray-600">Completed</Text>
          <Text className="text-2xl font-semibold text-green-600">
            {batches?.filter((b) => b.status === 'completed').length || 0}
          </Text>
        </Card>

        <Card className="p-4">
          <Text className="text-sm text-gray-600">Total Evaluations</Text>
          <Text className="text-2xl font-semibold">
            {batches?.reduce((sum, b) => sum + b.total_evaluations, 0) || 0}
          </Text>
        </Card>
      </div>

      {/* Batch List */}
      <div className="space-y-4">
        {batches?.map((batch) => (
          <Card key={batch.id} className="p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3">
                  <Heading level={3}>{batch.name}</Heading>
                  <Badge color={getStatusColor(batch.status)}>
                    <div className="flex items-center gap-1">
                      {getStatusIcon(batch.status)}
                      {batch.status}
                    </div>
                  </Badge>
                </div>

                {batch.description && (
                  <Text className="text-gray-600 mt-1">
                    {batch.description}
                  </Text>
                )}

                <div className="mt-4 flex items-center gap-6 text-sm">
                  <div>
                    <Text className="text-gray-500">Progress</Text>
                    <Text className="font-medium">
                      {batch.completed_evaluations} / {batch.total_evaluations}
                    </Text>
                  </div>

                  <div>
                    <Text className="text-gray-500">Success Rate</Text>
                    <Text className="font-medium">
                      {batch.total_evaluations > 0
                        ? Math.round(
                            ((batch.completed_evaluations -
                              batch.failed_evaluations) /
                              batch.total_evaluations) *
                              100,
                          )
                        : 0}
                      %
                    </Text>
                  </div>

                  {batch.started_at && (
                    <div>
                      <Text className="text-gray-500">Started</Text>
                      <Text className="font-medium">
                        {new Date(batch.started_at).toLocaleString()}
                      </Text>
                    </div>
                  )}

                  {batch.completed_at && (
                    <div>
                      <Text className="text-gray-500">Completed</Text>
                      <Text className="font-medium">
                        {new Date(batch.completed_at).toLocaleString()}
                      </Text>
                    </div>
                  )}
                </div>

                {/* Progress Bar */}
                {batch.status === 'running' && (
                  <div className="mt-4">
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${batch.progress_percentage}%` }}
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="flex items-center gap-2">
                {batch.status === 'pending' && (
                  <Button
                    onClick={() => handleStart(batch.id)}
                    className="flex items-center gap-1"
                  >
                    <PlayIcon className="h-4 w-4" />
                    Start
                  </Button>
                )}

                {batch.status === 'running' && (
                  <Button
                    color="red"
                    onClick={() => handleStop(batch.id)}
                    className="flex items-center gap-1"
                  >
                    <StopIcon className="h-4 w-4" />
                    Stop
                  </Button>
                )}

                {(batch.status === 'completed' ||
                  batch.status === 'failed' ||
                  batch.status === 'cancelled') && (
                  <Button
                    color="amber"
                    onClick={() => handleRestart(batch.id)}
                    className="flex items-center gap-1"
                  >
                    <ArrowPathIcon className="h-4 w-4" />
                    Restart
                  </Button>
                )}

                <Button
                  plain
                  onClick={() => setSelectedBatchId(batch.id)}
                  className="flex items-center gap-1"
                >
                  <ChartBarIcon className="h-4 w-4" />
                  Details
                </Button>
              </div>
            </div>
          </Card>
        ))}

        {(!batches || batches.length === 0) && (
          <Card className="p-12 text-center">
            <Text className="text-gray-500">
              No evaluation batches created yet
            </Text>
            <Button onClick={() => setIsCreateModalOpen(true)} className="mt-4">
              Create Your First Evaluation
            </Button>
          </Card>
        )}
      </div>

      {/* Create Modal */}
      <CreateEvaluationModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onCreateComplete={handleCreateComplete}
      />
    </div>
  );
};
